import { useMemo, useState } from 'react';
import { round2, priceForQty, priceLine, type Build, type BuildRow, type LineResult, type RowKind } from '../pricing/itemBuilder';
import { money, percent } from '../lib/format';
import { useLookups } from '../lib/lookups';
import NumInput from './NumInput';
import { Modal } from './ui';

export interface BuilderResult {
  build: Build;
  description: string;
  result: LineResult;
}

const newRow = (kind: RowKind): BuildRow => ({
  kind, description: '', buy_price: undefined, setup: kind === 'decoration' ? 0 : undefined,
  markup_percent: undefined, display_on_quote: false,
});

export default function ItemBuilder({ initial, description: initialDesc, customerMinMarkup, onApply, onClose }: {
  initial?: Build;
  description: string;
  customerMinMarkup: number | null;
  onApply: (r: BuilderResult) => void;
  onClose: () => void;
}) {
  const { decorations } = useLookups();
  const [build, setBuild] = useState<Build>(initial ?? { qty: 50, price_logic: 'cost_plus_markup', rows: [newRow('product'), newRow('decoration')] });
  const [description, setDescription] = useState(initialDesc);

  const margin = build.price_logic === 'set_gross_margin';

  const result = useMemo<LineResult | null>(() => {
    try {
      return build.qty > 0 ? priceLine(build, { customer_min_markup: customerMinMarkup ?? undefined }) : null;
    } catch {
      return null;
    }
  }, [build, customerMinMarkup]);

  const setRow = (i: number, patch: Partial<BuildRow>) =>
    setBuild((b) => ({ ...b, rows: b.rows.map((r, j) => (j === i ? { ...r, ...patch } : r)) }));
  const removeRow = (i: number) => setBuild((b) => ({ ...b, rows: b.rows.filter((_, j) => j !== i) }));
  const addRow = (kind: RowKind) => setBuild((b) => ({ ...b, rows: [...b.rows, newRow(kind)] }));

  const pickDecoration = (i: number, id: string) => {
    const d = decorations.find((x) => x.id === id);
    if (!d) return setRow(i, { decoration_id: undefined });
    const breaks = (d.price_breaks ?? []).filter((p) => p && p.qty != null);
    setRow(i, {
      decoration_id: d.id,
      description: d.name,
      vendor: d.vendors?.name,
      setup: d.setup_price,
      price_breaks: breaks.length ? breaks : undefined,
      buy_price: breaks.length ? priceForQty(breaks, build.qty) : undefined,
    });
  };

  const apply = () => {
    if (!result) return;
    const autoDesc = description.trim() || build.rows.filter((r) => r.kind === 'product').map((r) => r.description).filter(Boolean).join(', ');
    onApply({ build, description: autoDesc, result });
  };

  return (
    <Modal title="Item builder" onClose={onClose}
      footer={<>
        <span className="muted small grow">Unit sell = (buy + extra + setup ÷ qty) × (1 + markup), rounded to the cent.</span>
        <button onClick={onClose}>Cancel</button>
        <button className="primary" onClick={apply} disabled={!result}>Apply to quote line</button>
      </>}>
      <div className="stack">
        <div className="grid4">
          <label className="field" style={{ gridColumn: 'span 2' }}>
            <span>Line description (shown to the customer)</span>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="e.g. Natural calico tote, 1 colour print front" />
          </label>
          <label className="field">
            <span>Quantity</span>
            <NumInput value={build.qty} onChange={(v) => setBuild({ ...build, qty: v ?? 0 })} />
          </label>
          <label className="field">
            <span>Price logic</span>
            <select value={build.price_logic ?? 'cost_plus_markup'} onChange={(e) => setBuild({ ...build, price_logic: e.target.value as Build['price_logic'] })}>
              <option value="cost_plus_markup">Cost + markup</option>
              <option value="set_gross_margin">Set gross margin</option>
            </select>
          </label>
        </div>

        <div className="table-wrap">
          <table className="grid builder">
            <thead>
              <tr>
                <th>Type</th><th style={{ minWidth: 260 }}>Item</th><th className="num">Buy / unit</th><th className="num">Extra / unit</th>
                <th className="num">Setup</th><th className="num">{margin ? 'Margin %' : 'Markup %'}</th><th className="num">Sell override</th>
                <th className="num">Unit cost</th><th className="num">Unit sell</th><th className="num">Total</th><th></th>
              </tr>
            </thead>
            <tbody>
              {build.rows.map((r, i) => {
                const res = result?.rows[i];
                return (
                  <tr key={i}>
                    <td><span className={`kind ${r.kind}`}>{r.kind === 'additional' ? 'Extra' : r.kind}</span></td>
                    <td>
                      {r.kind === 'decoration' && (
                        <select value={r.decoration_id ?? ''} onChange={(e) => pickDecoration(i, e.target.value)} style={{ marginBottom: 4 }}>
                          <option value="">Choose decoration… (or type below)</option>
                          {decorations.map((d) => (
                            <option key={d.id} value={d.id}>{d.name}{d.vendors?.name ? ` — ${d.vendors.name}` : ''} (setup {money(d.setup_price)})</option>
                          ))}
                        </select>
                      )}
                      <input value={r.description} placeholder={r.kind === 'product' ? 'Product, e.g. Equality Natural tote' : r.kind === 'decoration' ? 'Decoration' : 'e.g. Freight, artwork'}
                        onChange={(e) => setRow(i, { description: e.target.value })} />
                      {r.kind === 'decoration' && (
                        <label className="check small muted" style={{ marginTop: 4 }}>
                          <input type="checkbox" checked={!!r.display_on_quote} onChange={(e) => setRow(i, { display_on_quote: e.target.checked })} />
                          Show as its own line on the quote
                        </label>
                      )}
                    </td>
                    <td><NumInput value={r.price_breaks ? res?.buy_unit : r.buy_price} disabled={!!r.price_breaks}
                      onChange={(v) => setRow(i, { buy_price: v })} style={{ width: 80 }} ariaLabel="Buy price" /></td>
                    <td><NumInput value={r.extra_per_unit} onChange={(v) => setRow(i, { extra_per_unit: v })} style={{ width: 70 }} ariaLabel="Extra per unit" /></td>
                    <td><NumInput value={r.setup} onChange={(v) => setRow(i, { setup: v })} style={{ width: 80 }} ariaLabel="Setup" /></td>
                    <td>{margin
                      ? <NumInput value={r.margin_percent} onChange={(v) => setRow(i, { margin_percent: v })} style={{ width: 70 }} ariaLabel="Margin" />
                      : <NumInput value={r.markup_percent} onChange={(v) => setRow(i, { markup_percent: v })} style={{ width: 70 }} ariaLabel="Markup" />}
                      {r.sell_override != null && res && <div className="small muted num">{percent(res.markup_percent)}</div>}
                    </td>
                    <td><NumInput value={r.sell_override} placeholder="—" onChange={(v) => setRow(i, { sell_override: v })} style={{ width: 80 }} ariaLabel="Sell override" /></td>
                    <td className="num calc">{res ? money(res.unit_cost) : ''}</td>
                    <td className="num"><b>{res ? money(res.unit_sell) : ''}</b></td>
                    <td className="num">{res ? money(res.total_sell) : ''}</td>
                    <td><button className="ghost small danger" onClick={() => removeRow(i)} aria-label="Remove row">✕</button></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="row">
          <button className="small" onClick={() => addRow('product')}>+ Product</button>
          <button className="small" onClick={() => addRow('decoration')}>+ Decoration</button>
          <button className="small" onClick={() => addRow('additional')}>+ Additional cost</button>
        </div>

        {result && (
          <div className="summary">
            <div><span>Total cost</span><b>{money(result.total_cost)}</b></div>
            <div><span>Total sell</span><b>{money(result.total_sell)}</b></div>
            <div><span>Sell / unit</span><b>{money(result.unit_sell)}</b></div>
            <div><span>Profit</span><b>{money(round2(result.total_sell - result.total_cost))}</b></div>
            <div><span>Gross margin</span><b>{percent(result.gross_margin_percent, 2)}</b></div>
          </div>
        )}
        {result?.warnings.map((w, i) => <div key={i} className="warn">{w}</div>)}
        {customerMinMarkup != null && <div className="muted small">This customer’s minimum markup is {customerMinMarkup}%.</div>}
      </div>
    </Modal>
  );
}
